import { motion } from "framer-motion";
import { Play, Pause, Clock, CalendarDays } from "lucide-react";
import { IMAGES, isSpecialEpisode } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";

export default function EpisodesSection({ episodes, nowPlaying, isPlaying, onPlay }) {
  return (
    <section id="episodes" data-testid="episodes-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.p variants={fadeUp} className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
            Latest Episodes
          </motion.p>
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <motion.h2
              variants={fadeUp}
              className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
            >
              Fresh off the <span className="font-garamond italic text-cherry">mic.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="max-w-sm text-sm leading-relaxed text-white/55">
              Press play for a preview, or catch the full episode on your
              favourite platform.
            </motion.p>
          </div>

          <motion.div variants={stagger} className="flex flex-col">
            {episodes.map((ep, i) => {
              const active = nowPlaying?.number === ep.number;
              return (
                <motion.article
                  key={ep.number}
                  variants={fadeUp}
                  data-testid={`episode-card-${i + 1}`}
                  className={`group flex items-center gap-5 border-t border-white/10 py-6 transition-colors duration-300 last:border-b hover:bg-white/[0.03] sm:gap-7 sm:py-7 ${
                    active ? "bg-white/[0.03]" : ""
                  }`}
                >
                  <span
                    className={`hidden font-garamond italic text-white/25 transition-colors group-hover:text-cherry sm:block sm:w-16 ${
                      isSpecialEpisode(ep) ? "text-2xl" : "text-4xl"
                    }`}
                  >
                    {isSpecialEpisode(ep) ? "IRL" : String(ep.number).padStart(2, "0")}
                  </span>

                  <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-2xl sm:h-24 sm:w-24">
                    <img
                      src={ep.art_url || IMAGES.cover}
                      alt={`City Girls Decoded — Episode ${ep.number} cover`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    {(ep.tags || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        {ep.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-full border border-cherry/35 bg-cherry/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-cherry"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3
                      className={`font-display text-base font-bold leading-snug sm:text-xl ${
                        ep.tags?.length ? "mt-2.5" : ""
                      }`}
                    >
                      {ep.title}
                    </h3>
                    {ep.description && (
                      <p className="mt-1.5 line-clamp-2 max-w-2xl text-xs leading-relaxed text-white/50 sm:text-sm">
                        {ep.description}
                      </p>
                    )}
                    <div className="mt-2.5 flex flex-wrap items-center gap-4 text-[11px] font-medium text-white/40">
                      {ep.duration && (
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" /> {ep.duration}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" /> {ep.released}
                      </span>
                    </div>
                  </div>

                  <button
                    data-testid={`episode-play-btn-${i + 1}`}
                    onClick={() => onPlay(ep)}
                    aria-label={active && isPlaying ? "Pause preview" : "Play preview"}
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-all duration-300 sm:h-14 sm:w-14 ${
                      active
                        ? "bg-cherry text-white shadow-[0_0_30px_rgba(255,0,85,0.45)]"
                        : "border border-white/20 text-white group-hover:border-cherry group-hover:bg-cherry group-hover:text-white"
                    }`}
                  >
                    {active && isPlaying ? (
                      <Pause className="h-5 w-5 fill-current" />
                    ) : (
                      <Play className="ml-0.5 h-5 w-5 fill-current" />
                    )}
                  </button>
                </motion.article>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
